import { useEffect } from "react";

export function publicSiteUrl(value = "/") {
  const configured = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://mindovia.space").trim();
  const origin = configured.replace(/\/+$/, "") || "https://mindovia.space";
  return new URL(value, `${origin}/`).toString();
}

/**
 * Lightweight SEO helper — updates <title> and meta description/OG
 * without pulling in react-helmet. Safe to call in any component.
 */
export default function useSEO({ title, description, url, image, robots, alternates }) {
  // Serialized so callers can pass a fresh object literal on every render.
  const alternatesKey = alternates ? JSON.stringify(alternates) : "";
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (attr, key, content) => {
      if (content == null) return;
      const matches = [...document.head.querySelectorAll(`meta[${attr}="${key}"]`)];
      let el = matches.shift();
      matches.forEach((duplicate) => duplicate.remove());
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const upsertLink = (rel, href) => {
      if (!href) return;
      const matches = [...document.head.querySelectorAll(`link[rel="${rel}"]`)];
      let el = matches.shift();
      matches.forEach((duplicate) => duplicate.remove());
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    const removeAll = (selector) => {
      document.head.querySelectorAll(selector).forEach((element) => element.remove());
    };

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (title) {
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (image) {
      const absoluteImage = publicSiteUrl(image);
      upsertMeta("property", "og:image", absoluteImage);
      upsertMeta("name", "twitter:image", absoluteImage);
    }
    if (url) {
      const absoluteUrl = publicSiteUrl(url);
      upsertMeta("property", "og:url", absoluteUrl);
      upsertLink("canonical", absoluteUrl);
    } else {
      removeAll('meta[property="og:url"]');
      removeAll('link[rel="canonical"]');
    }
    // hreflang alternates are rebuilt on every page so a client-side
    // navigation never leaves the previous page's language links behind.
    removeAll('link[rel="alternate"][hreflang]');
    if (url && alternatesKey) {
      Object.entries(JSON.parse(alternatesKey)).forEach(([hreflang, href]) => {
        if (!href) return;
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", hreflang);
        el.setAttribute("href", publicSiteUrl(href));
        document.head.appendChild(el);
      });
    }
    if (robots) {
      upsertMeta("name", "robots", robots);
      upsertMeta("name", "googlebot", robots);
    } else {
      // Reset to permissive default when navigating from a noindex page
      upsertMeta("name", "robots", "index, follow, max-image-preview:large");
      upsertMeta("name", "googlebot", "index, follow, max-image-preview:large");
    }
  }, [title, description, url, image, robots, alternatesKey]);
}
